import { notification } from "antd";
import { find, get, groupBy, orderBy, union } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAttachEmail,
  MdOutlineDownload,
  MdOutlineDownloadForOffline,
  MdOutlineEditNote,
} from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Modal from "shared/components/Modal";
import Loading from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DOCUMENT_STEPS,
  DOCUMENTS_GENERATION_MODES,
  DOCUMENTS_TYPES,
} from "utils/constants";
import {
  filterAttachmentsByStep,
  keepLatestAttachmentsByNameAndType,
} from "utils/helpers/documentStepsConfig";
import { downloadDocument, downloadFiles } from "utils/helpers/files";
import { STEPPER_KEYS } from "../../../..";
import File, { FILE_MODE } from "../elements/File";
import { generateDocumentsWithMode } from "../generateDocuments";

const isStaticAttachment = ({ mode, generate }) =>
  !generate && (!mode || mode === FILE_MODE.SIMPLE_FILE);

const Documents = ({
  additionalInfoPercentage,
  setActive,
  sendAttachments,
  data,
  subscription,
  downloadDocuments,
  downloadSingleDocument,
  generateSubscriptionDocuments,
  updateSubscription,
}) => {
  const [selectedData, setSelectedData] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const sendEsignRequest = ({ attachmentsId }) => {
    setLoading(true);
    setSelectedData([]);
    setIsAllChecked(false);
    const sendAttachmentsOptions = {
      variables: {
        where: { id: subscription.id },
        data: {
          attachmentsId,
          locked: true,
        },
      },
      onCompleted: () => {
        if (!subscription.locked)
          notification.open({
            message: DOCUMENTS.NOTIFICATION.SUCCESS,
            duration: 5,
            type: "success",
          });
        notification.open({
          message: DOCUMENTS.CANT_EDIT,
          duration: 5,
          type: "info",
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        notification.open({
          message: DOCUMENTS.NOTIFICATION.ERROR,
          duration: 5,
          type: "error",
        });
      },
    };
    return sendAttachments(sendAttachmentsOptions);
  };

  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", {
    returnObjects: true,
  });
  const DOCUMENTS = t("SUBSCRIPTION.DOCUMENT.DOCUMENTS", {
    returnObjects: true,
  });

  const generateDocuments = (mode, options) =>
    generateDocumentsWithMode({
      updateSubscription,
      generateSubscriptionDocuments,
      subscriptionId: get(subscription, "id"),
      mode,
      options: {
        variables: {
          data: { id: get(subscription, "id") },
        },
        ...options,
      },
    });

  const downloadAfterGenerate = (type) => {
    setLoading(true);
    generateDocuments(DOCUMENTS_GENERATION_MODES.DOWNLOAD, {
      onCompleted: ({ generateSubscriptionDocuments: { attachments } }) => {
        const document = find(attachments, { type });
        if (!document) {
          setLoading(false);
          return;
        }
        downloadSingleDocument({
          variables: { where: { id: document.id } },
          onCompleted: (_) => {
            const { document: downloaded } = omitDeep(
              _.downloadSingleDocument,
              "__typename",
            );
            downloadDocument(downloaded);
            setLoading(false);
          },
          onError: () => setLoading(false),
        });
      },
      onError: () => setLoading(false),
    });
  };

  const SECTIONS = useMemo(() => {
    const {
      MEMBERSHIP_FORM = [],
      DUTY_OF_ADVICE = [],
      DUTY_OF_ADVICE_QUOTE = [],
      SEPA_DIRECT_DEBIT_MANDATE = [],
      TERMINATION_LETTER = [],
      TERMINATION_MANDATE = [],
      IPID = [],
      INFO_NOTICE = [],
      BROCHURE = [],
      STATUSES = [],
      QUOTE = [],
    } = groupBy(
      keepLatestAttachmentsByNameAndType(
        filterAttachmentsByStep({
          attachments: union(data, subscription?.attachments),
          params: get(subscription, "insurancePlan.params", []),
          step: DOCUMENT_STEPS.CONTRAT,
        }),
      ),
      "type",
    );

    const buildEditableFile = ({ type, defaultName, lockedFiles = [] }) => {
      if (subscription.locked && lockedFiles.length) {
        return lockedFiles.map(({ name, ...rest }) => ({
          ...rest,
          filename: name,
          mode: FILE_MODE.SIMPLE_FILE,
        }));
      }

      return [
        {
          id: `dynamic-${type}`,
          filename: defaultName,
          documentType: type,
          mode: FILE_MODE.FILE_WITH_PERCENTAGE,
          percentageLabel: `${additionalInfoPercentage}% ${SUBSCRIPTION.DONE}`,
          percentage: additionalInfoPercentage,
          generate: true,
          actions: [
            ...(additionalInfoPercentage < 100
              ? [
                  {
                    icon: <MdOutlineEditNote size={16} />,
                    text: "Remplir",
                    onClick: () =>
                      setActive(STEPPER_KEYS.ADDITIONAL_INFORMATION),
                  },
                ]
              : []),
          ],
          disabled: additionalInfoPercentage < 100,
          onClick: () => downloadAfterGenerate(type),
        },
      ];
    };

    const chronologicalQuotes = orderBy(
      [...DUTY_OF_ADVICE_QUOTE, ...QUOTE],
      ["createdDate", "name"],
      ["asc", "asc"],
    );

    return [
      {
        title: DOCUMENTS.MEMBERSHIP_APPLICATION,
        isChecked: true,
        files: [
          ...buildEditableFile({
            type: DOCUMENTS_TYPES.MEMBERSHIP_FORM,
            defaultName: "Bulletin d'adhésion",
            lockedFiles: MEMBERSHIP_FORM,
          }),
          ...buildEditableFile({
            type: DOCUMENTS_TYPES.DUTY_OF_ADVICE,
            defaultName: "Devoir de conseil (FIC)",
            lockedFiles: DUTY_OF_ADVICE,
          }),
          ...buildEditableFile({
            type: DOCUMENTS_TYPES.SEPA_DIRECT_DEBIT_MANDATE,
            defaultName: "Mandat SEPA",
            lockedFiles: SEPA_DIRECT_DEBIT_MANDATE,
          }),
        ],
      },
      {
        title: DOCUMENTS.TERMINATION_DOCUMENTS,
        isChecked: true,
        isRia: true,
        files: [
          ...buildEditableFile({
            type: DOCUMENTS_TYPES.TERMINATION_LETTER,
            defaultName: "Lettre de résiliation",
            lockedFiles: TERMINATION_LETTER,
          }),
          ...buildEditableFile({
            type: DOCUMENTS_TYPES.TERMINATION_MANDATE,
            defaultName: "Mandat de résiliation",
            lockedFiles: TERMINATION_MANDATE,
          }),
        ],
      },
      {
        title: DOCUMENTS.QUOTES,
        files: chronologicalQuotes.map(({ name, ...rest }) => ({
          ...rest,
          filename: name,
          mode: FILE_MODE.SIMPLE_FILE,
        })),
      },
      {
        title: DOCUMENTS.NOTICES,
        files: [
          ...INFO_NOTICE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
            mode: FILE_MODE.SIMPLE_FILE,
          })),
          ...IPID.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
            mode: FILE_MODE.SIMPLE_FILE,
          })),
          ...BROCHURE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
            mode: FILE_MODE.SIMPLE_FILE,
          })),
          ...STATUSES.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
            mode: FILE_MODE.SIMPLE_FILE,
          })),
        ],
      },
    ];
  }, [subscription, additionalInfoPercentage, data]);

  const onCheck = (record, state) => {
    setSelectedData((prev) => {
      const next = state
        ? [...prev, record]
        : [...prev.filter((item) => item !== record)];
      setIsAllChecked(
        next.length ===
          SECTIONS.reduce((acc, { files = [] }) => acc + files.length, 0),
      );
      return next;
    });
  };

  const selectAll = (e) => {
    setIsAllChecked(e.target.checked);
    if (!e.target.checked) {
      setSelectedData([]);
      return;
    }
    setSelectedData(
      SECTIONS.filter(
        ({ isRia }) => !isRia || isRia === get(subscription, "project.ria"),
      ).flatMap(({ files = [] }) =>
        files.filter(
          ({ mode, percentage, disabled }) =>
            !disabled &&
            (percentage === 100 || mode !== FILE_MODE.FILE_WITH_PERCENTAGE),
        ),
      ),
    );
  };

  const downloadSelected = () => {
    if (
      selectedData.find(({ mode }) => mode === FILE_MODE.FILE_WITH_PERCENTAGE)
    )
      return setVisible(true);
    setLoading(true);
    const documents = selectedData.map((document) => document.id);
    downloadDocuments({
      variables: {
        data: {
          isIn: {
            id: documents,
          },
        },
      },
      onCompleted: (_) => {
        const { base64 } = omitDeep(_.download, "__typename");
        downloadFiles(base64);
        setSelectedData([]);
        setIsAllChecked(false);
        setLoading(false);
      },
    });
  };

  const download = (file) => {
    const { id, generate } = file;
    if (generate) return file.onClick();

    setLoading(true);
    downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
        setLoading(false);
      },
    });
  };

  const onSubmitModal = async () => {
    setLoading(true);
    await generateDocuments(DOCUMENTS_GENERATION_MODES.DOWNLOAD, {
      onCompleted: ({
        generateSubscriptionDocuments: { attachments, base64 },
      }) => {
        const documents = selectedData
          .map((document) => document.id)
          .concat(...attachments.map(({ id }) => id));
        if (documents.length) {
          downloadDocuments({
            variables: {
              data: {
                isIn: {
                  id: documents,
                },
              },
            },
            onCompleted: ({ download: { base64: zipBase64 } }) => {
              downloadFiles(zipBase64);
              setIsAllChecked(false);
            },
          });
        } else downloadFiles(base64);

        setSelectedData([]);
        setLoading(false);
        setVisible(false);
        notification.open({
          message: DOCUMENTS.CANT_EDIT,
          duration: 5,
          type: "info",
        });
      },
    });
    setLoading(false);
    setVisible(false);
  };

  const buildAttachmentsId = (generatedAttachments = []) => {
    const staticAttachmentIds = selectedData
      .filter(isStaticAttachment)
      .map(({ id }) => id);
    const selectedDynamicTypes = selectedData
      .filter(({ mode }) => mode === FILE_MODE.FILE_WITH_PERCENTAGE)
      .map(({ documentType }) => documentType)
      .filter(Boolean);

    const generatedAttachmentIds = generatedAttachments
      .filter(({ type }) =>
        selectedDynamicTypes.length
          ? selectedDynamicTypes.includes(type)
          : false,
      )
      .map(({ id }) => id);

    return [...staticAttachmentIds, ...generatedAttachmentIds];
  };

  const onSend = async () => {
    const hasDynamicFiles = selectedData.some(
      ({ mode }) => mode === FILE_MODE.FILE_WITH_PERCENTAGE,
    );

    if (hasDynamicFiles) {
      setLoading(true);
      return generateDocuments(DOCUMENTS_GENERATION_MODES.EMAIL, {
        onCompleted: ({ generateSubscriptionDocuments: { attachments } }) =>
          sendEsignRequest({
            attachmentsId: buildAttachmentsId(attachments),
          }),
        onError: () => {
          setLoading(false);
          notification.open({
            message: DOCUMENTS.NOTIFICATION.ERROR,
            duration: 5,
            type: "error",
          });
        },
      });
    }

    sendEsignRequest({ attachmentsId: buildAttachmentsId() });
  };

  if (loading) return <Loading />;

  return (
    <div className="subscription-form--wrapper">
      <Modal
        mode="simple"
        type="warning"
        okText={DOCUMENTS.MODAL.OK}
        cancelText={DOCUMENTS.MODAL.CANCEL}
        onCancel={() => setVisible(false)}
        onOk={onSubmitModal}
        message={DOCUMENTS.MODAL.MESSAGE}
        open={visible}
      />
      <Form type="vertical" title={DOCUMENTS.TITLE}>
        <div className="snippet_wrapper">
          <span className="snippet_wrapper--title">
            {DOCUMENTS.SNIPPET.TITLE}
          </span>
          <span className="snippet_wrapper--description">
            {DOCUMENTS.SNIPPET.DESCRIPTION}
          </span>

          <div className="snippet_wrapper--detail">
            {/* TODO(acoris): postal address is client-paramétrable; expose via
                org/process config once the ACORIS referential is available. */}
            <div className="snippet_wrapper--detail__row">
              <span>{DOCUMENTS.SNIPPET.BY_MAIL}</span>
              <span>
                {DOCUMENTS.SNIPPET.POSTAL_ADDRESS ||
                  "Acoris Mutuelles - Service Adhésions"}
              </span>
            </div>
          </div>
        </div>
        <div className="document-list_wrapper">
          <div className="document-list_wrapper--actions">
            <div className="right_column">
              <Input
                type="checkbox"
                onChange={selectAll}
                label={DOCUMENTS.SELECT_ALL}
                checked={isAllChecked}
              />
            </div>
            <div className="left_column">
              <Button
                type="primary--link"
                alignment="center"
                disabled={!selectedData.length}
                onClick={onSend}
              >
                <MdOutlineAttachEmail size={16} />
                {DOCUMENTS.SEND}
              </Button>
              <Button
                type="primary--link"
                alignment="center"
                onClick={downloadSelected}
                disabled={!selectedData.length}
              >
                <MdOutlineDownload size={16} />
                {DOCUMENTS.DOWNLOAD}
              </Button>
            </div>
          </div>
          {SECTIONS.map(
            (item, index) =>
              (!item.isRia ||
                item.isRia === get(subscription, "project.ria")) && (
                <div
                  className="document-list_wrapper--file-section"
                  key={`section-${index}`}
                >
                  <span className="document-list_wrapper--file-section__title">
                    {item.title}
                  </span>
                  <div className="document-list_wrapper--file-section__content">
                    {item.files.map((file, fileIndex) => (
                      <div className="file_wrapper" key={`file-${fileIndex}`}>
                        <div className="file_wrapper--metadata">
                          <div className="file_wrapper--metadata__content">
                            <Input
                              type="checkbox"
                              checked={
                                !!selectedData.find(
                                  (item) => item.id === file.id,
                                )
                              }
                              onChange={(e) => onCheck(file, e.target.checked)}
                              disabled={file.disabled}
                            />
                            <File element={file} mode={file.mode} />
                          </div>
                        </div>
                        <div className="file_wrapper--action">
                          <Button
                            disabled={file.disabled}
                            onClick={() => download(file)}
                          >
                            <MdOutlineDownloadForOffline size={20} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
          )}
        </div>
      </Form>
    </div>
  );
};

export default Documents;
